import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { ordersApi, type Order } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import {
  isOrderAlreadyCancelledError,
  isOrderCancellationBlockedError,
} from "@/lib/orderCancellationError";

/**
 * `POST /orders/{id}/cancel`. O 200 cobre os dois desfechos: pedido cancelado (`cancelledAt`) ou
 * chamado aberto no suporte (`cancellationTicketId`, pedido segue ativo). A seção decide o que
 * mostrar a partir do pedido devolvido, então basta gravá-lo no cache da tela de detalhe — mesclado
 * com o anterior, porque a resposta pode vir sem relações (ex.: `customer`) e deixar a tela em branco.
 */
export const useCancelOrder = (orderId: string, orderQueryKey: QueryKey) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reason: string) => ordersApi.cancel(orderId, reason),
    onSuccess: (updatedOrder) => {
      queryClient.setQueryData(orderQueryKey, (previous: Order | null | undefined) =>
        previous ? { ...previous, ...updatedOrder } : updatedOrder
      );
    },
    // Pedido já cancelado por outra via, ou API antiga que respondia 409 + `zendeskTicketId`:
    // recarrega o pedido (e segura o `isPending` até lá) pra seção refletir o estado real.
    onError: (error) => {
      const alreadyCancelled = isOrderAlreadyCancelledError(error);
      if (alreadyCancelled) {
        toast.info("Pedido já cancelado", { description: alreadyCancelled.message });
        return queryClient.invalidateQueries({ queryKey: orderQueryKey });
      }
      if (isOrderCancellationBlockedError(error)?.zendeskTicketId) {
        return queryClient.invalidateQueries({ queryKey: orderQueryKey });
      }
    },
  });
};
